import { useMemo } from "react";
import {
  TicketEnrichment,
  TicketExploreRow,
} from "../../../api/useExplore/Explore.model";
import { DataList } from "../../../components/shared/data-list";
import { DataListValue } from "../../../components/shared/data-list/DataList.model";
import styles from "./TicketQAScore.module.scss";

type TicketOverviewProps = TicketExploreRow & { hasRelease?: boolean } & {
  enrichments?: TicketEnrichment[];
};

function TicketOverview({
  overview,
  agent,
  agentAvgQaScore,
  collaborators,
  avgQaScore,
  topic,
  status,
  kbCoverage,
  channel,
  ttr,
  category,
  ticketCsat,
  hasRelease = false,
  isValidated,
  avgSentiment,
  organization,
  createdAt,
  updatedAt,
  enrichments,
}: TicketOverviewProps) {
  const generalDataList = useMemo<DataListValue[]>(() => {
    const list: DataListValue[] = [
      { title: "Agent", type: "agent", value: agent },
      { title: "Agent QA Score", type: "qaScore", value: agentAvgQaScore },
      {
        title: "Collaborators",
        type: "collaborators",
        value: collaborators,
      },
      { title: "QA Score", type: "qaScore", value: avgQaScore },
      {
        title: "Validation",
        type: "ticket-validation",
        value: isValidated,
      },
    ];

    return list;
  }, [agent, collaborators, avgQaScore, isValidated]);

  const ticketDataList = useMemo<DataListValue[]>(() => {
    const list: DataListValue[] = [
      { title: "Topic", type: "string", value: topic },
      { title: "Organization", type: "string", value: organization },
      { title: "Ticket Status", type: "status", value: status },
      { title: "KB Coverage", type: "kbCoverage", value: kbCoverage },
      { title: "Channel", type: "channel", value: channel },
      { title: "TTR", type: "ttr", value: ttr },
      { title: "Category", type: "category", value: category },
      { title: "CSAT", type: "qaScore", value: ticketCsat },
      { title: "Agent Autonomous", type: "release", value: hasRelease },
      { title: "Sentiment", type: "avgSentiment", value: avgSentiment },
      { title: "Created At", type: "date", value: createdAt },
      { title: "Last Update", type: "date", value: updatedAt },
    ];

    return list.filter((item) => item.value != null);
  }, [
    topic,
    status,
    kbCoverage,
    channel,
    ttr,
    category,
    ticketCsat,
    hasRelease,
    avgSentiment,
  ]);

  const enrichmentsDataList = useMemo<DataListValue[]>(
    () =>
      enrichments
        ?.sort((a, b) => {
          if (a.enrichmentId === b.enrichmentId) {
            return (
              new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
            );
          }
          return a.enrichmentId.localeCompare(b.enrichmentId);
        })
        .filter(
          (x, index, self) =>
            index === self.findIndex((t) => t.enrichmentId === x.enrichmentId)
        )
        .map((x) => {
          return {
            title: x.label,
            type: "enrichment",
            value: {
              label: x.value,
              reason: x.reason,
            },
          };
        }) ?? [],
    [enrichments]
  );

  const displayId = overview?.displayId ?? overview.id;
  return (
    <div className={styles.TicketOverview}>
      <div className={styles.Header}>
        <div className={styles.Subject}>{overview.subject}</div>
        <div className={styles.TicketId}>{`#${displayId}`}</div>
      </div>

      <div>
        <DataList className={styles.DataList} values={generalDataList} />
        <DataList className={styles.DataList} values={ticketDataList} />
        <DataList className={styles.DataList} values={enrichmentsDataList} />
      </div>
    </div>
  );
}

export default TicketOverview;
